import aiService, { type AiTaskDispatched } from "@/api/ai";

interface RunAiTaskOptions {
    pollIntervalMs?: number;
    maxAttempts?: number;
}

/**
 * Run one of the queued AI endpoints end to end: dispatch the task, then
 * poll its status until it completes. Resolves with the task result, or
 * rejects when the task fails or polling exhausts its attempts. The
 * AI work runs on the queue worker, so the HTTP requests involved are
 * always short.
 */
export async function runAiTask<T>(
    dispatch: () => Promise<AiTaskDispatched>,
    { pollIntervalMs = 1500, maxAttempts = 120 }: RunAiTaskOptions = {},
): Promise<T> {
    const { task_id: taskId } = await dispatch();

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

        const task = await aiService.getAiTask<T>(taskId);

        if (task.status === "completed") {
            return task.result as T;
        }

        if (task.status === "failed") {
            throw new Error(task.error || "aiTask.failed");
        }
    }

    throw new Error("aiTask.timeout");
}
